import * as localForage from 'localforage';
import { from, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

export interface DatabaseConfig {
  databaseName: string;
  objectStoreName: string;
}

export interface Entity {
  id: string;
}

export class Queue<T extends Entity> {
  private entityStore: LocalForage | null = null;
  private queueOrderStore: LocalForage | null = null;

  constructor(config: DatabaseConfig) {
    const entityStore = localForage.createInstance({
      name: config.databaseName,
      storeName: config.objectStoreName,
    });

    const queueStore = localForage.createInstance({
      name: config.databaseName,
      storeName: `${config.objectStoreName}-queue`,
    });

    this.entityStore = entityStore;
    this.queueOrderStore = queueStore;
  }

  enqueue(item: T) {
    const objectStore = this.getEntityStore();
    const queueStore = this.getQueueOrderStore();
    const queueOrder = from(queueStore.getItem<string[]>('queue'));

    return queueOrder.pipe(
      switchMap((queueOrder) => {
        const updatedQueueOrder = [item.id, ...(queueOrder ?? [])];
        queueStore.setItem('queue', updatedQueueOrder);

        return from(objectStore.setItem(item.id, item));
      })
    );
  }

  dequeue() {
    const objectStore = this.getEntityStore();
    const queueStore = this.getQueueOrderStore();
    const queueOrder = from(queueStore.getItem<string[]>('queue'));

    return queueOrder.pipe(
      switchMap((queueOrder) => {
        const itemId = (queueOrder ?? [])[0];
        queueStore.setItem(
          'queue',
          (queueOrder ?? []).filter((x) => x !== itemId)
        );

        if (!itemId) {
          return of(null);
        }

        return from(objectStore.getItem<T | null>(itemId)).pipe(
          switchMap((item) => {
            return from(objectStore.removeItem(itemId)).pipe(
              switchMap(() => {
                return of(item);
              })
            );
          })
        );
      })
    );
  }

  peek(): Observable<T | null> {
    return from(this._peek());
  }

  private async _peek(): Promise<T | null> {
    const objectStore = this.getEntityStore();
    const keys = await objectStore.keys();

    const items: T[] = [];

    for (const key of keys) {
      const item = await objectStore.getItem<T>(key);
      items.push(item as T);
    }

    return items[0] ?? null;
  }

  private getEntityStore() {
    if (!this.entityStore) {
      throw new Error('IndexedDb Database not defined');
    }
    return this.entityStore;
  }

  private getQueueOrderStore() {
    if (!this.queueOrderStore) {
      throw new Error('IndexedDb Database not defined');
    }
    return this.queueOrderStore;
  }
}
